import { AgendaGradeLinha } from '../../../interfaces/agenda';
import { AgendaAcaoAgendamento, motivoAcaoLinha, motivoAcaoSelecao } from './agenda-acoes';
import { ehMarcado, temPaciente } from './agenda-grade/agenda-grade';

export type AgendaMenuAcao =
  | 'novo' | 'encaixe' | 'reservar' | 'liberar'
  | 'alterar' | 'trocarPaciente' | 'transferir' | 'desvincular' | 'cancelar'
  | 'abrirAtendimento' | 'conta' | 'historico' | 'filaMedica';

/** Mesma regra habilita/desabilita o item no menu e o botão da barra (Encaixe, Fila médica). */
export interface AgendaMenuRegra {
  pode: boolean;
  motivo: string;
}
export type AgendaMenuEncaixe = AgendaMenuRegra;

/**
 * Ver Fila do dashboard: o back só monta a fila de hoje na unidade logada, então outra data ou
 * outra unidade abriria a fila errada.
 */
export function regraFilaMedica(
  cdAgenda: number | null | undefined, data: string, cdEstabelecimento: number | null, unidadeLogada: number | null, hoje: string,
): AgendaMenuRegra {
  if (!cdAgenda) return { pode: false, motivo: 'Escolha uma agenda' };
  if (data !== hoje) return { pode: false, motivo: 'A Fila médica mostra só o dia de hoje' };
  if (unidadeLogada && cdEstabelecimento !== unidadeLogada) return { pode: false, motivo: 'A Fila médica é só da unidade em que você está' };
  return { pode: true, motivo: '' };
}

const SEM_FILA_MEDICA: AgendaMenuRegra = { pode: false, motivo: 'Escolha uma agenda' };

export interface AgendaMenuItem {
  acao: AgendaMenuAcao;
  rotulo: string;
  desabilitado: boolean;
  /** Por que está desabilitado. */
  motivo?: string;
  /** Subtítulo de item habilitado. */
  dica?: string;
  perigo?: boolean;
}

export interface AgendaMenuLinha {
  /** Ações sobre o horário em si (novo/reservar) ou o agendamento (alterar/transferir/cancelar...). */
  principais: AgendaMenuItem[];
  /** "Já existe na Fila": atalhos para telas que a Fila já tem. */
  fila: AgendaMenuItem[];
}

const SO_SUPERVISAO = 'Somente supervisão';

const DICAS: Partial<Record<AgendaAcaoAgendamento, string>> = {
  alterar: 'Status, classificação, convênio, especialidade…',
};

/**
 * Monta o menu ⋮/botão direito de uma linha: topo depende do status (livre/reservado vs
 * agendamento), e o grupo "Já existe na Fila" sempre aparece, desabilitado quando faltar paciente
 * ou atendimento. `encaixe` e `filaMedica` vêm de fora porque dependem do filtro e da unidade, que
 * moram no painel — aqui só se lê o resultado, sem recalcular.
 */
export function itensMenuLinha(
  l: AgendaGradeLinha, supervisor: boolean, encaixe: AgendaMenuEncaixe, filaMedica: AgendaMenuRegra = SEM_FILA_MEDICA,
): AgendaMenuLinha {
  const principais: AgendaMenuItem[] = [];
  const temPac = temPaciente(l);

  if (l.ieStatusAgenda === 'L' && !temPac) {
    const rotuloNovo = l.tipo === 'EXAME' ? 'Agendar exame' : 'Novo agendamento';
    principais.push({ acao: 'novo', rotulo: rotuloNovo, desabilitado: false });
    principais.push({ acao: 'encaixe', rotulo: 'Encaixe', desabilitado: !encaixe.pode, motivo: encaixe.pode ? undefined : encaixe.motivo });
    principais.push({ acao: 'reservar', rotulo: 'Reservar horário', desabilitado: !supervisor, motivo: supervisor ? undefined : SO_SUPERVISAO });
  } else if (l.ieStatusAgenda === 'R' && !temPac) {
    principais.push({ acao: 'novo', rotulo: 'Agendar no horário reservado', desabilitado: !supervisor, motivo: supervisor ? undefined : SO_SUPERVISAO });
    principais.push({ acao: 'encaixe', rotulo: 'Encaixe', desabilitado: !encaixe.pode, motivo: encaixe.pode ? undefined : encaixe.motivo });
    principais.push({ acao: 'liberar', rotulo: 'Liberar reserva', desabilitado: !supervisor, motivo: supervisor ? undefined : SO_SUPERVISAO });
  } else {
    const item = (acao: AgendaAcaoAgendamento, rotulo: string, perigo?: boolean): AgendaMenuItem => {
      const motivo = motivoAcaoLinha(acao, l);
      return {
        acao, rotulo, desabilitado: !!motivo,
        ...(motivo ? { motivo } : DICAS[acao] ? { dica: DICAS[acao] } : {}),
        ...(perigo ? { perigo } : {}),
      };
    };
    principais.push(item('alterar', 'Alterar agendamento'));
    principais.push(item('trocarPaciente', 'Trocar paciente'));
    principais.push(item('transferir', 'Transferir / remarcar'));
    principais.push(item('desvincular', 'Desvincular conta'));
    principais.push(item('cancelar', 'Cancelar agendamento', true));
  }

  const semPaciente = !temPac;
  const motivoSemPaciente = 'Sem paciente neste horário';
  const fila: AgendaMenuItem[] = [
    l.paciente?.nrAtendimento
      ? { acao: 'conta', rotulo: 'Conta do paciente', desabilitado: false }
      // A tela de abrir atendimento só sabe partir da senha chamada (selectedPassword); abrir daqui
      // carregaria o paciente da senha, não o da linha.
      : { acao: 'abrirAtendimento', rotulo: 'Abrir atendimento', desabilitado: true, motivo: semPaciente ? motivoSemPaciente : 'Abra o atendimento pela senha do paciente na Fila' },
    { acao: 'historico', rotulo: 'Histórico do paciente', desabilitado: semPaciente, motivo: semPaciente ? motivoSemPaciente : undefined },
    { acao: 'filaMedica', rotulo: 'Fila médica', desabilitado: !filaMedica.pode, motivo: filaMedica.pode ? undefined : filaMedica.motivo },
  ];

  return { principais, fila };
}

/**
 * Menu combinado quando o clique direito mantém uma multi-seleção: mesmas regras e rótulos da
 * barra (motivoAcaoSelecao, contagem no rótulo), sem atalhos "Já existe na Fila" porque não há um
 * paciente só.
 */
export function itensMenuSelecao(linhas: AgendaGradeLinha[]): AgendaMenuItem[] {
  const n = linhas.filter(ehMarcado).length;
  const item = (acao: AgendaAcaoAgendamento, rotulo: string, contavel: boolean, perigo?: boolean): AgendaMenuItem => {
    const motivo = motivoAcaoSelecao(acao, linhas);
    const sufixo = contavel && n > 1 ? ` (${n})` : '';
    return {
      acao, rotulo: rotulo + sufixo, desabilitado: !!motivo,
      ...(motivo ? { motivo } : {}),
      ...(perigo ? { perigo } : {}),
    };
  };
  return [
    item('alterar', 'Alterar agendamento', true),
    item('trocarPaciente', 'Trocar paciente', false),
    item('transferir', 'Transferir / remarcar', true),
    item('desvincular', 'Desvincular conta', false),
    item('cancelar', 'Cancelar agendamento', true, true),
  ];
}

/** Clique direito: mantém o menu dentro da janela, como um tooltip de canto. */
export function posicaoMenu(x: number, y: number, larguraJanela: number, alturaJanela: number): { top: number; left: number } {
  const LARGURA = 260;
  const ALTURA_EST = 320;
  const MARGEM = 8;
  const left = Math.min(Math.max(x, MARGEM), Math.max(MARGEM, larguraJanela - LARGURA - MARGEM));
  const top = Math.min(Math.max(y, MARGEM), Math.max(MARGEM, alturaJanela - ALTURA_EST - MARGEM));
  return { top, left };
}
